/**
 * Outil « Couleurs et absorbance » (Boîte à outils, physique-chimie 1re et Tle) — logique PURE.
 *
 * Trois parties :
 * - Additive (1re) : trois projecteurs rouge, vert, bleu sur un écran noir ; mélanges, cônes de
 *   l'œil stimulés (vision des couleurs, écrans).
 * - Soustractive (1re) : une lampe (R, V, B), jusqu'à deux filtres, un objet coloré ; ce qui est
 *   absorbé, transmis, diffusé, et la couleur perçue.
 * - Absorbance (1re, Tle) : une solution colorée au spectrophotomètre, spectre d'absorption,
 *   couleur perçue (complémentaire), loi de Beer-Lambert A = ε × ℓ × c, dosage par étalonnage.
 *
 * Modèle des synthèses : trois composantes (R, V, B), présentes ou non.
 */

import { frNum, mcqOptions, pick, retryMcq, sigFixed, type Mcq, type Rng } from '../../ui/mcq';

export const fr = frNum;
const NB = String.fromCharCode(0xa0);

export type Part = 'additive' | 'subtractive' | 'absorbance';

export const PARTS: ReadonlyArray<{ id: Part; label: string }> = [
  { id: 'additive', label: 'Additive' },
  { id: 'subtractive', label: 'Soustractive' },
  { id: 'absorbance', label: 'Absorbance' },
];

export function partsFor(rank: number | null) {
  return PARTS.filter((p) => p.id === 'absorbance' || rank === null || rank <= 6);
}

/* ---------- composantes ---------- */

export type Comp = 'R' | 'V' | 'B';
export const COMPS: readonly Comp[] = ['R', 'V', 'B'];
export type Set3 = Readonly<Record<Comp, boolean>>;

export const set3 = (r: boolean, v: boolean, b: boolean): Set3 => ({ R: r, V: v, B: b });
export const inter = (...xs: Set3[]): Set3 =>
  set3(
    xs.every((x) => x.R),
    xs.every((x) => x.V),
    xs.every((x) => x.B),
  );

const KEY = (s: Set3) => `${+s.R}${+s.V}${+s.B}`;

export const NAMES: Record<string, string> = {
  '000': 'noir',
  '100': 'rouge',
  '010': 'vert',
  '001': 'bleu',
  '110': 'jaune',
  '011': 'cyan',
  '101': 'magenta',
  '111': 'blanc',
};

export const nameOf = (s: Set3) => NAMES[KEY(s)]!;
export const hexOf = (s: Set3) => `#${s.R ? 'ff' : '00'}${s.V ? 'ff' : '00'}${s.B ? 'ff' : '00'}`;
export const complement = (s: Set3): Set3 => set3(!s.R, !s.V, !s.B);
export const compsText = (s: Set3) => {
  const xs = COMPS.filter((c) => s[c]).map((c) => COMP_NAME[c]);
  return xs.length === 0 ? 'aucune' : xs.join(' + ');
};
export const COMP_NAME: Record<Comp, string> = { R: 'rouge', V: 'vert', B: 'bleu' };

/** set par son nom de couleur */
export const setOf = (name: string): Set3 => {
  const k = Object.keys(NAMES).find((x) => NAMES[x] === name)!;
  return set3(k[0] === '1', k[1] === '1', k[2] === '1');
};

/** nom d'une couleur quelconque (r, v, b de 0 à 1) */
export function colorName(r: number, g: number, b: number): string {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max < 0.08) return 'noir';
  const s = (max - min) / max;
  if (s < 0.15) return max > 0.85 ? 'blanc' : 'gris';
  let h: number;
  if (max === r) h = (60 * ((g - b) / (max - min)) + 360) % 360;
  else if (max === g) h = 60 * ((b - r) / (max - min)) + 120;
  else h = 60 * ((r - g) / (max - min)) + 240;
  const name =
    h < 15 || h >= 345
      ? 'rouge'
      : h < 45
        ? 'orange'
        : h < 70
          ? 'jaune'
          : h < 160
            ? 'vert'
            : h < 200
              ? 'cyan'
              : h < 255
                ? 'bleu'
                : h < 290
                  ? 'violet'
                  : 'magenta';
  if (max < 0.45) return `${name} sombre`;
  return min / max > 0.35 ? `${name} clair` : name;
}

export const rgbCss = (r: number, g: number, b: number) =>
  `rgb(${Math.round(255 * r)}, ${Math.round(255 * g)}, ${Math.round(255 * b)})`;

export function additiveLines(r: number, g: number, b: number): string[] {
  const v = { R: r, V: g, B: b };
  const binary = COMPS.every((c) => v[c] < 0.02 || v[c] > 0.98);
  const out: string[] = [];
  if (binary) {
    const on = set3(r > 0.5, g > 0.5, b > 0.5);
    const n = COMPS.filter((c) => on[c]).length;
    out.push(
      n === 0
        ? 'Aucun projecteur allumé : l’écran reste noir.'
        : `Synthèse additive : ${compsText(on)} donne ${nameOf(on)}.`,
    );
    if (n === 2)
      out.push(
        `${nameOf(on)[0]!.toUpperCase()}${nameOf(on).slice(1)} et ${nameOf(complement(on))} sont complémentaires : ensemble, ils donnent du blanc.`,
      );
    if (n === 3) out.push('Les trois lumières primaires, à égale intensité, donnent du blanc.');
  } else {
    const parts = COMPS.filter((c) => v[c] >= 0.02).map(
      (c) => `${COMP_NAME[c]} ${frNum(100 * v[c], 0)}${NB}%`,
    );
    out.push(
      `Synthèse additive : ${parts.join(' + ')} donne ${colorName(r, g, b)}. Les proportions comptent, pas seulement les couleurs allumées.`,
    );
  }
  out.push(
    'L’œil a trois sortes de cônes (sensibles au rouge, au vert, au bleu) : la couleur perçue dépend de la façon dont chacun est stimulé. Un écran n’allume que des pixels rouges, verts et bleus.',
  );
  return out;
}

/* ---------- soustractive ---------- */

export interface Thing {
  id: string;
  label: string;
  /** ce que l'objet diffuse (ou ce que le filtre transmet) */
  keeps: Set3;
}

export const OBJECTS: readonly Thing[] = [
  { id: 'snow', label: 'Neige (blanche)', keeps: set3(true, true, true) },
  { id: 'tomato', label: 'Tomate (rouge)', keeps: set3(true, false, false) },
  { id: 'leaf', label: 'Feuille (verte)', keeps: set3(false, true, false) },
  { id: 'ink', label: 'Encre (bleue)', keeps: set3(false, false, true) },
  { id: 'lemon', label: 'Citron (jaune)', keeps: set3(true, true, false) },
  { id: 'pool', label: 'Bouée (cyan)', keeps: set3(false, true, true) },
  { id: 'flower', label: 'Fleur (magenta)', keeps: set3(true, false, true) },
  { id: 'coal', label: 'Charbon (noir)', keeps: set3(false, false, false) },
];

export const FILTERS: readonly Thing[] = [
  { id: 'none', label: 'aucun', keeps: set3(true, true, true) },
  { id: 'cyan', label: 'cyan', keeps: set3(false, true, true) },
  { id: 'magenta', label: 'magenta', keeps: set3(true, false, true) },
  { id: 'yellow', label: 'jaune', keeps: set3(true, true, false) },
  { id: 'red', label: 'rouge', keeps: set3(true, false, false) },
  { id: 'green', label: 'vert', keeps: set3(false, true, false) },
  { id: 'blue', label: 'bleu', keeps: set3(false, false, true) },
];

export interface Scene {
  light: Set3;
  filters: readonly Thing[];
  object: Thing;
}

export function perceived(sc: Scene) {
  const afterFilters = inter(sc.light, ...sc.filters.map((f) => f.keeps));
  const seen = inter(afterFilters, sc.object.keeps);
  return { afterFilters, seen };
}

export function subtractiveLines(sc: Scene): string[] {
  const { afterFilters, seen } = perceived(sc);
  const out = [`Lumière de la lampe : ${compsText(sc.light)} (${nameOf(sc.light)}).`];
  const fs = sc.filters.filter((f) => f.id !== 'none');
  if (fs.length > 0)
    out.push(
      `Filtre${fs.length > 1 ? 's' : ''} ${fs.map((f) => f.label).join(' puis ')} : ${fs.length > 1 ? 'chacun ne laisse passer que sa couleur ; ' : ''}il reste ${compsText(afterFilters)} (${nameOf(afterFilters)}).`,
    );
  const absorbed = inter(afterFilters, complement(sc.object.keeps));
  out.push(
    `L’objet ${sc.object.label.toLowerCase()} diffuse ${compsText(sc.object.keeps)} et absorbe le reste${COMPS.some((c) => absorbed[c]) ? ` : ici il absorbe ${compsText(absorbed)}` : ''}.`,
  );
  out.push(
    seen.R || seen.V || seen.B
      ? `Il renvoie vers l’œil ${compsText(seen)} : il paraît ${nameOf(seen)}.`
      : 'Il ne renvoie aucune lumière : il paraît noir.',
  );
  return out;
}

/* ---------- absorbance ---------- */

export interface Solution {
  id: string;
  label: string;
  formula: string;
  /** nm */
  lmax: number;
  /** largeur de la bande (nm) */
  sigma: number;
  /** L·mol⁻¹·cm⁻¹ au maximum */
  eps: number;
  /** concentration maximale du curseur (mol/L) et son unité d'affichage */
  cmax: number;
  unit: 'mmol' | 'µmol';
}

export const SOLUTIONS: readonly Solution[] = [
  {
    id: 'permanganate',
    label: 'Permanganate',
    formula: 'MnO₄⁻',
    lmax: 525,
    sigma: 38,
    eps: 2400,
    cmax: 5e-4,
    unit: 'mmol',
  },
  {
    id: 'copper',
    label: 'Sulfate de cuivre',
    formula: 'Cu²⁺',
    lmax: 800,
    sigma: 110,
    eps: 12,
    cmax: 0.1,
    unit: 'mmol',
  },
  {
    id: 'e133',
    label: 'Bleu E133',
    formula: 'E133',
    lmax: 630,
    sigma: 32,
    eps: 130_000,
    cmax: 1e-5,
    unit: 'µmol',
  },
  {
    id: 'e102',
    label: 'Jaune E102',
    formula: 'E102',
    lmax: 427,
    sigma: 40,
    eps: 27_000,
    cmax: 5e-5,
    unit: 'µmol',
  },
];

export const L_CM = 1; // cuve de 1 cm

/** coefficient d'absorption molaire à λ (bande gaussienne) */
export const epsAt = (s: Solution, l: number) =>
  s.eps * Math.exp(-((l - s.lmax) ** 2) / (2 * s.sigma ** 2));

/** absorbance A = ε(λ) × ℓ × c */
export const absorbance = (s: Solution, l: number, c: number, len = L_CM) => epsAt(s, l) * len * c;

/** couleur d'une radiation (nm) */
export function wavelengthName(l: number): string {
  if (l < 400) return 'ultraviolet';
  if (l < 435) return 'violet';
  if (l < 490) return 'bleu';
  if (l < 510) return 'cyan';
  if (l < 560) return 'vert';
  if (l < 590) return 'jaune';
  if (l < 620) return 'orange';
  if (l <= 780) return 'rouge';
  return 'infrarouge';
}

/** teinte affichable d'une radiation (approximative) */
export function wavelengthRgb(l: number): [number, number, number] {
  let r = 0;
  let g = 0;
  let b = 0;
  if (l >= 380 && l < 440) {
    r = (440 - l) / 60;
    b = 1;
  } else if (l < 490) {
    g = (l - 440) / 50;
    b = 1;
  } else if (l < 510) {
    g = 1;
    b = (510 - l) / 20;
  } else if (l < 580) {
    r = (l - 510) / 70;
    g = 1;
  } else if (l < 645) {
    r = 1;
    g = (645 - l) / 65;
  } else if (l <= 780) r = 1;
  return [Math.max(0, r), Math.max(0, g), Math.max(0, b)];
}

/** couleur complémentaire de la radiation la plus absorbée (étoile des couleurs) */
export function complementaryOf(l: number): string {
  if (l < 435) return 'jaune-vert';
  if (l < 490) return 'jaune';
  if (l < 510) return 'rouge';
  if (l < 560) return 'magenta';
  if (l < 590) return 'bleu';
  if (l < 620) return 'bleu';
  if (l <= 780) return 'cyan';
  return 'bleu pâle';
}

/** couleur de la solution en lumière blanche : lumière transmise filtrée par les trois cônes */
export function solutionColor(s: Solution, c: number, len = L_CM): [number, number, number] {
  const cone = (center: number, sigma: number) => {
    let num = 0;
    let den = 0;
    for (let l = 400; l <= 750; l += 5) {
      const w = Math.exp(-((l - center) ** 2) / (2 * sigma ** 2));
      num += w * 10 ** -absorbance(s, l, c, len);
      den += w;
    }
    return num / den;
  };
  return [cone(600, 40), cone(545, 35), cone(450, 30)];
}

export const concText = (s: Solution, c: number) =>
  s.unit === 'mmol'
    ? `${fr(c * 1000, c * 1000 < 1 ? 3 : 2)}${NB}mmol/L`
    : `${fr(c * 1e6, 2)}${NB}µmol/L`;

export const absText = (A: number) => sigFixed(A, A < 0.1 ? 1 : 2);

/** solution « inconnue » à doser : 37 % de la concentration maximale */
export const unknownC = (s: Solution) => 0.37 * s.cmax;

export function absorbanceLines(s: Solution, l: number, c: number, rank: number | null): string[] {
  const A = absorbance(s, l, c);
  const out = [
    s.lmax > 780
      ? `Maximum d’absorption vers ${s.lmax}${NB}nm (infrarouge proche) ; dans le visible, la solution absorbe surtout le rouge : elle paraît bleu-cyan.`
      : `La solution absorbe surtout vers ${s.lmax}${NB}nm (${wavelengthName(s.lmax)}) : elle paraît ${complementaryOf(s.lmax)}, la couleur complémentaire.`,
    `À ${l}${NB}nm : A = ε × ℓ × c = ${fr(epsAt(s, l), 0)}${NB}L·mol⁻¹·cm⁻¹ × 1${NB}cm × ${sci(c)}${NB}mol/L = ${absText(A)}.`,
  ];
  if (Math.abs(l - s.lmax) > 2 * s.sigma)
    out.push(
      `Loin du maximum d’absorption, A est faible : pour doser, on se place à λ_max = ${s.lmax}${NB}nm.`,
    );
  out.push(
    rank === null || rank >= 7
      ? 'Loi de Beer-Lambert : à λ fixée, A est proportionnelle à c (et à ℓ) ; la droite d’étalonnage A = k × c sert au dosage.'
      : 'A est proportionnelle à la concentration : deux fois plus concentrée, deux fois plus d’absorbance.',
  );
  return out;
}

const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
export function sci(v: number): string {
  if (v === 0) return '0';
  let e = Math.floor(Math.log10(Math.abs(v)));
  let m = v / 10 ** e;
  if (Number(m.toPrecision(2)) >= 10) {
    e += 1;
    m /= 10;
  }
  const p = `10${e < 0 ? '⁻' : ''}${String(Math.abs(e)).replace(/\d/g, (d) => SUP[Number(d)]!)}`;
  return e === 0 ? sigFixed(m, 2) : `${sigFixed(m, 2)}${NB}×${NB}${p}`;
}

/* ---------- défi ---------- */

export type QuizKind =
  | 'additive'
  | 'complement'
  | 'object-light'
  | 'filters'
  | 'absorbed'
  | 'beer'
  | 'calibration'
  | 'length';

export type Quiz = Mcq<QuizKind>;

export function quizKinds(rank: number | null): QuizKind[] {
  if (rank === null) return ['additive', 'object-light', 'absorbed', 'beer', 'calibration'];
  if (rank >= 7) return ['absorbed', 'beer', 'calibration', 'length', 'complement'];
  return ['additive', 'complement', 'object-light', 'filters', 'absorbed'];
}

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  return retryMcq((r) => buildQuiz(kind, r), rng);
}

type W = readonly [string, string];
const q = (
  kind: QuizKind,
  question: string,
  good: string,
  wrongs: W[],
  explain: string,
  rng: Rng,
): Quiz => ({ kind, question, options: mcqOptions(good, wrongs, rng), explain });

const cap = (s: string) => `${s[0]!.toUpperCase()}${s.slice(1)}`;
/** « lumière verte » */
const LIGHT: Record<string, string> = { rouge: 'rouge', vert: 'verte', bleu: 'bleue' };
const ALL = ['noir', 'rouge', 'vert', 'bleu', 'jaune', 'cyan', 'magenta', 'blanc'];

function buildQuiz(kind: QuizKind, rng: Rng): Quiz {
  switch (kind) {
    case 'additive': {
      const [x, y] = pick(
        [
          ['rouge', 'vert'],
          ['vert', 'bleu'],
          ['rouge', 'bleu'],
        ] as const,
        rng,
      );
      const sx = setOf(x);
      const sy = setOf(y);
      const sum = set3(sx.R || sy.R, sx.V || sy.V, sx.B || sy.B);
      const good = nameOf(sum);
      return q(
        kind,
        `Sur un écran blanc, on superpose une lumière ${LIGHT[x]} et une lumière ${LIGHT[y]}. Quelle couleur voit-on ?`,
        cap(good),
        [
          ['Noir', 'En synthèse additive, les lumières s’ajoutent : pas de noir.'],
          ['Blanc', 'Le blanc demande les trois lumières primaires.'],
          [cap(nameOf(complement(sum))), `C’est la couleur complémentaire du ${good}.`],
          ['Marron', 'Ce sont des lumières, pas des peintures : elles s’ajoutent.'],
        ],
        `Synthèse additive : ${x} + ${y} = ${good}.`,
        rng,
      );
    }
    case 'complement': {
      const name = pick(['rouge', 'vert', 'bleu', 'jaune', 'cyan', 'magenta'], rng);
      const good = nameOf(complement(setOf(name)));
      return q(
        kind,
        `Quelle est la couleur complémentaire du ${name} ?`,
        cap(good),
        ALL.filter((x) => x !== good && x !== name && x !== 'noir' && x !== 'blanc').map(
          (x) =>
            [cap(x), `${cap(name)} + ${x} ne donnent pas du blanc en synthèse additive.`] as const,
        ),
        `${cap(name)} + ${good} = blanc : ce sont des couleurs complémentaires.`,
        rng,
      );
    }
    case 'object-light': {
      const obj = pick(
        OBJECTS.filter((o) => o.id !== 'snow' && o.id !== 'coal'),
        rng,
      );
      const light = pick(['rouge', 'vert', 'bleu'] as const, rng);
      const back = inter(obj.keeps, setOf(light));
      const seen = nameOf(back);
      return q(
        kind,
        `Un objet paraît ${nameOf(obj.keeps)} en lumière blanche. Quelle couleur a-t-il en lumière ${LIGHT[light]} ?`,
        cap(seen),
        [
          [
            cap(nameOf(obj.keeps)),
            'La couleur d’un objet dépend aussi de la lumière qui l’éclaire.',
          ],
          [cap(light), `L’objet absorbe le ${light} : il ne le renvoie pas.`],
          ['Noir', `L’objet diffuse le ${light} : il le renvoie.`],
          ['Blanc', 'Un objet ne renvoie que la lumière qu’il reçoit et qu’il diffuse.'],
        ],
        `L’objet diffuse ${compsText(obj.keeps)} ; il ne reçoit que du ${light} : il renvoie ${compsText(back)}, il paraît ${seen}.`,
        rng,
      );
    }
    case 'filters': {
      const [a, b] = pick(
        [
          ['cyan', 'jaune'],
          ['magenta', 'jaune'],
          ['cyan', 'magenta'],
          ['jaune', 'rouge'],
        ] as const,
        rng,
      );
      const good = nameOf(inter(setOf(a), setOf(b)));
      return q(
        kind,
        `Lumière blanche, puis un filtre ${a}, puis un filtre ${b}. Quelle est la couleur de la lumière qui sort ?`,
        cap(good),
        [
          ['Blanc', 'Chaque filtre absorbe une partie de la lumière.'],
          [
            cap(nameOf(complement(inter(setOf(a), setOf(b))))),
            'C’est la couleur absorbée, pas celle qui passe.',
          ],
          ['Noir (rien ne passe)', 'Les deux filtres laissent passer une couleur en commun.'],
          [cap(a), `Le filtre ${b} agit aussi.`],
        ],
        `${cap(a)} laisse passer ${compsText(setOf(a))} ; ${b} laisse passer ${compsText(setOf(b))} : il reste ${compsText(inter(setOf(a), setOf(b)))}, soit ${good}.`,
        rng,
      );
    }
    case 'absorbed': {
      const [l, good] = pick(
        [
          [450, 'jaune'],
          [530, 'magenta'],
          [600, 'bleu'],
          [650, 'cyan'],
        ] as const,
        rng,
      );
      return q(
        kind,
        `Le spectre d’absorption d’une solution a un maximum vers ${l}${NB}nm (${wavelengthName(l)}). Couleur de la solution ?`,
        cap(good),
        [
          [cap(wavelengthName(l)), 'C’est la couleur ABSORBÉE : on voit sa complémentaire.'],
          ['Incolore', 'Une solution qui absorbe dans le visible est colorée.'],
          ['Noir', 'Elle n’absorbe qu’une partie du visible.'],
        ],
        `Absorbe le ${wavelengthName(l)} : paraît ${good}, la couleur complémentaire (étoile des couleurs).`,
        rng,
      );
    }
    case 'beer': {
      const eps = pick([2400, 1200, 6000], rng);
      const A = pick([0.36, 0.48, 0.6], rng);
      const c = A / eps;
      return q(
        kind,
        `Cuve de 1,0${NB}cm, ε = ${fr(eps, 0)}${NB}L·mol⁻¹·cm⁻¹, A = ${fr(A, 2)}. Concentration c ?`,
        `${sci(c)}${NB}mol/L`,
        [
          [`${sci(A * eps)}${NB}mol/L`, 'c = A ÷ (ε × ℓ), on divise.'],
          [`${sci(eps / A)}${NB}mol/L`, 'c = A ÷ (ε × ℓ), pas l’inverse.'],
          [`${sci(c * 1000)}${NB}mol/L`, 'Attention aux puissances de 10.'],
        ],
        `c = A ÷ (ε × ℓ) = ${fr(A, 2)} ÷ (${fr(eps, 0)} × 1,0) = ${sci(c)}${NB}mol/L.`,
        rng,
      );
    }
    case 'calibration': {
      const [c1, A1] = pick(
        [
          [2, 0.4],
          [5, 0.5],
          [4, 0.32],
        ] as const,
        rng,
      );
      const Ax = pick([1.5, 2.5, 0.5], rng) * A1;
      const cx = (Ax / A1) * c1;
      return q(
        kind,
        `Étalonnage : une solution à ${c1}${NB}mmol/L a pour absorbance ${fr(A1, 2)}. La solution inconnue a A = ${fr(Ax, 2)} (même λ, même cuve). Sa concentration ?`,
        `${fr(cx, 2)}${NB}mmol/L`,
        [
          [
            `${fr((A1 / Ax) * c1, 2)}${NB}mmol/L`,
            'A est proportionnelle à c : plus d’absorbance, plus concentrée.',
          ],
          [
            `${fr(c1 + (Ax - A1), 2)}${NB}mmol/L`,
            'C’est une proportionnalité, pas une différence.',
          ],
          [`${fr(c1, 2)}${NB}mmol/L`, 'Les absorbances sont différentes.'],
        ],
        `A = k × c : c = ${fr(Ax, 2)} ÷ ${fr(A1, 2)} × ${c1} = ${fr(cx, 2)}${NB}mmol/L.`,
        rng,
      );
    }
    case 'length': {
      const A = pick([0.2, 0.3, 0.45], rng);
      const k = pick([2, 0.5], rng);
      return q(
        kind,
        `Une solution a une absorbance de ${fr(A, 2)} dans une cuve de 1${NB}cm. Dans une cuve de ${fr(k, 1)}${NB}cm ?`,
        fr(A * k, 3),
        [
          [fr(A, 3), 'A dépend de la longueur ℓ traversée : A = ε × ℓ × c.'],
          [fr(A / k, 3), 'A est proportionnelle à ℓ, pas inversement.'],
          [fr(A + k, 3), 'Proportionnalité, pas addition.'],
        ],
        `A = ε × ℓ × c : ℓ multipliée par ${fr(k, 1)}, A aussi : ${fr(A * k, 3)}.`,
        rng,
      );
    }
  }
}
